/* eslint-disable no-eval */
import { getPageRoute } from "./Config.js";
import { FREE_CALENDAR_LIMIT } from "../components/discovery/stanzaPlusProps.js";

/**
 * FeatureGatingService
 *
 * Centralized service for managing consumer feature access control and upgrade flows.
 * Similar to Config.js for routes, this provides a single source of truth for:
 * - Feature access requirements (consumer/Stanza Plus features)
 * - Upgrade trigger configurations
 *
 * This service handles two types of checks:
 * 1. ENTITLEMENT-BASED: Checks subscription tier/auth status (e.g., "Does user have Stanza Plus?")
 * 2. STATE-BASED: Checks integration/connection status (e.g., "Is Google Calendar connected?")
 * 3. HYBRID: Combines both entitlement and state checks
 *
 * ============================================================================
 * USAGE PATTERNS
 * ============================================================================
 *
 * This service provides PURE FUNCTIONS with no side effects (no payments, no navigation).
 * For React components with side effects, use the useFeatureAccess hook instead.
 *
 * PATTERN 1: UI Variation (Show/Hide Based on Current State)
 * ────────────────────────────────────────────────────────────
 * Use when: You need to display different UI based on what user currently has
 * Examples: Show "Force Refresh" vs "Resubscribe", show/hide upgrade banners
 *
 *   import { Features, hasAccess } from 'src/services/FeatureGatingService'
 *
 *   {hasAccess(user, Features.GOOGLE_CALENDAR_SYNC.key) ? (
 *     <Button>Force Refresh</Button>
 *   ) : (
 *     <Button>Resubscribe</Button>
 *   )}
 *
 *   {!hasAccess(user, Features.CREATE_GROUP.key) && (
 *     <UpgradeBanner />
 *   )}
 *
 * PATTERN 2: Feature Gating (User Wants to Take Action)
 * ───────────────────────────────────────────────────────
 * Use when: User is trying to perform an action (subscribe, add to group, etc.)
 * IMPORTANT: Use the FeatureAccessContext instead of calling these functions directly!
 *
 *   // ❌ DON'T DO THIS (missing simple payment):
 *   if (!hasAccess(user, Features.ADD_TO_GROUP.key)) {
 *     const upgradeFlow = getUpgradeFlow(Features.ADD_TO_GROUP.key, { calendar });
 *     navigate(upgradeFlow.route, { state: upgradeFlow.state });
 *   }
 *
 *   // ✅ DO THIS INSTEAD:
 *   import { useFeatureAccess } from 'src/utils/FeatureAccessContext'
 *   import { Features } from 'src/services/FeatureGatingService'
 *
 *   const { requestFeatureAccess } = useFeatureAccess();
 *
 *   const handleAction = async () => {
 *     const performAction = () => doSomething();
 *
 *     const result = await requestFeatureAccess(Features.ADD_TO_GROUP.key, {
 *       calendar,
 *       currentPath: window.location.pathname,
 *       action: performAction,
 *       continueText: "Continue with Action"
 *     });
 *
 *     if (result.granted && result.reason === "has_access") {
 *       performAction();
 *     }
 *   };
 *
 * Why use FeatureAccessContext for actions?
 * - Automatically attempts simple payment for eligible users
 * - Handles payment success/failure gracefully
 * - Shows success dialog and executes action callback
 * - Falls back to upgrade flow if needed
 * - Consistent behavior across the codebase
 *
 * See /utils/FeatureAccessContext.js for complete documentation.
 *
 * ============================================================================
 * QUICK REFERENCE
 * ============================================================================
 *
 * | Need                          | Use                      | Where                       |
 * |-------------------------------|--------------------------|----------------------------|
 * | Check state for UI            | hasAccess()              | FeatureGatingService.js    |
 * | Get upgrade route config      | getUpgradeFlow()         | FeatureGatingService.js    |
 * | Handle user action            | requestFeatureAccess()   | FeatureAccessContext.js    |
 */

// ============================================================================
// CONSUMER FEATURE DEFINITIONS
// ============================================================================

/**
 * Feature Definition Interface
 *
 * Each feature extends this base structure:
 * {
 *   // REQUIRED FIELDS:
 *   type: "ENTITLEMENT" | "STATE" | "HYBRID",  // Type of access check
 *   key: string,                                // Unique feature identifier
 *   name: string,                               // Human-readable feature name
 *
 *   // OPTIONAL FIELDS (with defaults):
 *   requiresAuth: boolean,                      // Default: true
 *   requiresSubscription: boolean | object,     // Default: true (can be dynamic condition object)
 *   requiresGoogleConnection: boolean,          // Default: false
 *   followedCreatorExempt: boolean,             // Default: false — a target calendar whose
 *                                               // creator the user already follows opens no new slot
 *
 *   // UPGRADE FLOW FIELDS (optional):
 *   upgradeType: string,                        // Type of upgrade flow to trigger
 *   upgradeSource: string,                      // Analytics source tag for upgrade
 *   upgradeRoute: string,                       // Custom upgrade route (overrides default routing logic)
 * }
 *
 * Feature Types:
 * - ENTITLEMENT: Checks subscription tier/auth only
 * - STATE: Checks integration/connection state only
 * - HYBRID: Checks both entitlement and state
 */
export const Features = Object.freeze({
  // ==================== CALENDAR FEATURES ====================
  ADD_CALENDAR: {
    type: "ENTITLEMENT", // Subscription tier check (dynamic based on calendar count)
    key: "ADD_CALENDAR",
    name: "Add Calendar",
    requiresSubscription: {
      "(user?.consumerSlotsUsed ?? (user?.addedCalendars.length + user?.addedGroups.length))": ">=2",
    }, // Dynamic: requires subscription when the user's slots (same-creator calendars share one) are full
    followedCreatorExempt: true,
    requiresGoogleConnection: false,
    upgradeType: "CALENDAR_LIMIT",
    upgradeSource: "add_calendar_limit",
    upgradeRoute: "UPGRADE_TRIGGER",
  },

  RESUBSCRIBE_CALENDAR: {
    type: "HYBRID", // Subscription tier check (dynamic based on calendar count)
    key: "RESUBSCRIBE_CALENDAR",
    name: "Add Calendar",
    requiresSubscription: {
      "(user?.consumerSlotsUsed ?? (user?.addedCalendars.length + user?.addedGroups.length))": ">2",
    }, // Dynamic: requires subscription when the user's slots (same-creator calendars share one) are past full
    requiresGoogleConnection: false,
    upgradeType: "CALENDAR_LIMIT",
    upgradeSource: "add_calendar_limit",
    upgradeRoute: "UPGRADE_TRIGGER",
  },

  ADD_EXCLUSIVE_CALENDAR: {
    type: "ENTITLEMENT", // Subscription tier check
    key: "ADD_EXCLUSIVE_CALENDAR",
    name: "Add Exclusive Calendars",
    requiresGoogleConnection: false,
    upgradeType: "STANZA_PLUS_CALENDAR",
    upgradeSource: "exclusive_calendar",
    upgradeRoute: "UPGRADE_TRIGGER",
  },

  ADD_EVENT: {
    type: "ENTITLEMENT", // Subscription tier check
    key: "ADD_EVENT",
    name: "Add Events",
    requiresGoogleConnection: false,
    upgradeType: "INDIVIDUAL_EVENT_ADD",
    upgradeSource: "unlimited_events",
    upgradeRoute: "UPGRADE_TRIGGER",
  },

  // ==================== GOOGLE CALENDAR FEATURES ====================
  GOOGLE_CALENDAR_SYNC: {
    type: "HYBRID", // Checks both subscription AND active Google Calendar connection
    key: "GOOGLE_CALENDAR_SYNC",
    name: "Google Calendar Sync",
    requiresGoogleConnection: true,
    upgradeType: "STANZA_PLUS",
    upgradeSource: "google_calendar_sync",
  },

  CONNECT_GOOGLE_CALENDAR: {
    type: "ENTITLEMENT", // Checks subscription tier (eligibility to start connection)
    key: "CONNECT_GOOGLE_CALENDAR",
    name: "Connect Google Calendar",
    requiresGoogleConnection: false,
    upgradeType: "STANZA_PLUS",
    upgradeSource: "connect_google_calendar",
    upgradeRoute: "SUBSCRIBING_GOOGLE_CALENDAR_STEPS", // Custom route for Google Calendar upgrade flow
  },

  CONNECT_OTHER_CALENDAR: {
    type: "ENTITLEMENT", // Checks subscription tier (eligibility to start connection)
    key: "CONNECT_OTHER_CALENDAR",
    name: "Connect Other Calendar",
    requiresGoogleConnection: false,
    upgradeType: "STANZA_PLUS",
    upgradeSource: "connect_other_calendar",
    upgradeRoute: "SUBSCRIBING_CALENDAR_STEPS", // Route for other calendar apps (Apple Calendar, Outlook, etc.)
  },

  // ==================== GROUP FEATURES ====================
  CREATE_GROUP: {
    type: "ENTITLEMENT", // Subscription tier check
    key: "CREATE_GROUP",
    name: "Create Group",
    requiresGoogleConnection: false,
    upgradeType: "STANZA_PLUS",
    upgradeSource: "create_group",
  },

  ADD_TO_GROUP: {
    type: "ENTITLEMENT", // Subscription tier check
    key: "ADD_TO_GROUP",
    name: "Add to Group",
    requiresGoogleConnection: false,
    upgradeType: "STANZA_PLUS",
    upgradeSource: "add_to_group",
  },

  // ==================== PERSONALIZATION ====================
  PERSONALIZE_CALENDAR: {
    type: "ENTITLEMENT", // Subscription tier check
    key: "PERSONALIZE_CALENDAR",
    name: "Customize Calendar",
    requiresGoogleConnection: false,
    upgradeType: "CALENDAR_PERSONALIZATION",
    upgradeSource: "personalization_sheet",
    upgradeRoute: "UPGRADE_TRIGGER", // Honors `forward`, so the user lands back on the open sheet
  },

  // ==================== SUBSCRIPTION EXPERIENCE ====================
  SKIP_SUBSCRIPTION_EXPERIENCE: {
    type: "ENTITLEMENT", // Subscription tier check (determines flow routing)
    key: "SKIP_SUBSCRIPTION_EXPERIENCE",
    name: "Skip Subscription Experience",
    requiresGoogleConnection: false,
  },

  // ==================== STANDARD UPGRADE ============================
  STANDARD_UPGRADE: {
    type: "ENTITLEMENT",
    key: "STANDARD_UPGRADE",
    name: "Stanza+ Upgrade",
    upgradeType: "STANZA_PLUS",
    upgradeSource: "consumer_upgrade_button",
  }
});

// ============================================================================
// ACCESS CHECK FUNCTIONS
// ============================================================================

/**
 * Whether adding `calendar` would take a new free-plan slot: false only for a
 * calendar (not a group) whose handle already holds one of the user's slots.
 * @param {Object} user - The user object
 * @param {Object} [calendar] - The calendar or group being added
 * @returns {boolean}
 */
export const opensNewCalendarSlot = (user, calendar) =>
  !!calendar?.calendars ||
  !(calendar?.handle && user?.consumerSlotHandles?.includes(calendar.handle));

/**
 * Whether adding `calendar` stays within the free slots — the FE mirror of the
 * server's `exceedsSlotLimitAfterAdd`.
 * @param {Object} user - The user object
 * @param {Object} [calendar] - The calendar being added
 * @returns {boolean}
 */
export const fitsFreeSlots = (user, calendar) => {
  const slotsUsed =
    user?.consumerSlotsUsed ??
    (user?.addedCalendars?.length || 0) + (user?.addedGroups?.length || 0);
  return slotsUsed + (opensNewCalendarSlot(user, calendar) ? 1 : 0) <= FREE_CALENDAR_LIMIT;
};

/**
 * Check if a user has access to a specific feature
 * @param {Object} user - The user object
 * @param {string} featureKey - Key from Features object
 * @param {Object} [options]
 * @param {Object} [options.calendar] - Target calendar, for `followedCreatorExempt` features
 * @returns {boolean} - True if user has access
 *
 * Note: requiresAuth and requiresSubscription default to true unless explicitly set to false
 */
export const hasAccess = (user, featureKey, { calendar } = {}) => {
  const feature = Features[featureKey];

  if (!feature) {
    console.warn(`Feature "${featureKey}" not defined in FeatureGatingService`);
    return true;
  }

  // Default requiresAuth to true unless explicitly set to false
  const requiresAuth = feature.requiresAuth !== false;
  if (requiresAuth && !user) return false;

  // Default requiresSubscription to true unless explicitly set to false
  const requiresSubscription = feature.requiresSubscription !== false;

  if (typeof feature.requiresSubscription === "boolean") {
    if (feature.requiresSubscription && !user?.hasValidSubscription)
      return false;
  } else if (typeof feature.requiresSubscription === "object") {
    const [key, condition] = Object.entries(feature.requiresSubscription)[0];
    const expr = key + condition;
    const exempt = feature.followedCreatorExempt && fitsFreeSlots(user, calendar);
    if (eval(expr) && !exempt && !user?.hasValidSubscription) return false;
  } else if (requiresSubscription && !user?.hasValidSubscription) {
    // Default case: requiresSubscription is undefined, so default to true
    return false;
  }

  if (feature.requiresGoogleConnection && !user?.hasValidGoogleConnection)
    return false;

  return true;
};

/**
 * Check if a user needs to upgrade to access a feature
 * @param {Object} user - The user object
 * @param {string} featureKey - Key from Features object
 * @returns {boolean} - True if user needs to upgrade
 */
export const requiresUpgrade = (user, featureKey) => {
  return !hasAccess(user, featureKey);
};

/**
 * Check if user is a Google Calendar connection candidate
 * (Signed in with Google but hasn't connected calendar)
 */
export const isGoogleCalendarConnectionCandidate = (user) => {
  return (
    user?.provider === "google" &&
    !user?.hasValidGoogleConnection &&
    !user?.hasPendingGoogleDisconnect
  );
};

// ============================================================================
// USER STRIPE SUBSCRIPTIONS CHECKS
// ============================================================================

// Check if user is stanza+
export const hasValidConsumerSubscription = (user) => {
  if (user) return user.hasValidSubscription;
  return false;
};

// Check if user is stanza+ o creator
export const hasSomeActiveSubscription = (user) => {
  if (user)
    return !!user.hasValidSubscription || !!user.hasValidCreatorSubscription;
  return false;
};

// Check if user has a plan to manage, including one lapsed by a failed payment
export const canManageSubscription = (user) =>
  hasSomeActiveSubscription(user) || !!user?.hasBillingPortalAccess;

// Check if user can upgrade to stanza+
export const needsSomeUpgrade = (user) => {
  if (user) return !user.hasValidSubscription;
  return false;
};

// Check if user has a valid stanza+ o creator subscription
export const isFreeUser = (user) => {
  if (user)
    return (
      !user.hasValidSubscription &&
      !user.hasValidCreatorSubscription &&
      !user.enterpriseCreator
    );
  return false;
};

// Check if user should go through subscription experience flow
export const shouldUseSubscriptionExperience = (user) => {
  return !hasValidConsumerSubscription(user);
};

// Rename for understaiding, same as shouldUseSubscriptionExperience
export const willSubscribeWithExperience = (user) =>
  shouldUseSubscriptionExperience(user);

// ============================================================================
// UPGRADE FLOW FUNCTIONS
// ============================================================================

/**
 * Get upgrade flow information for a feature
 * @param {string} featureKey - Key from Features object
 * @param {Object} options - Additional options (calendar, currentPath, successUrl, etc.)
 * @returns {Object} - Upgrade flow configuration { route, state, queryParams, upgradeType }
 */
export const getUpgradeFlow = (featureKey, options = {}) => {
  const feature = Features[featureKey];
  const { calendar, currentPath, successUrl } = options;

  if (!feature) {
    console.warn(`Feature "${featureKey}" not defined in FeatureGatingService`);
    return null;
  }

  // Build forward URL for returning after upgrade
  const forwardUrl =
    successUrl ||
    currentPath ||
    window.location.pathname + window.location.search;

  // Base query params
  const queryParams = {
    forward: forwardUrl,
  };

  // Add upgrade type if needed
  if (feature.upgradeType) {
    queryParams.upgradeType = feature.upgradeType;
  }

  // Build state object
  const state = {
    source: feature.upgradeSource || featureKey.toLowerCase(),
    calendar: calendar || null,
  };

  // Determine which upgrade route to use
  let route;

  if (feature.upgradeRoute) {
    // Use custom upgradeRoute if defined
    if (feature.upgradeRoute === "UPGRADE_TRIGGER") {
      // UPGRADE_TRIGGER uses query params
      route = getPageRoute("upgrade", "UPGRADE_TRIGGER", {}, queryParams);
    } else if (feature.upgradeRoute === "EASY_SUBSCRIBE") {
      const handle = calendar?.handle;
      if (handle && feature.easySubscribeSrc) {
        const easySubscribeQueryParams = { src: feature.easySubscribeSrc };
        if (calendar?._id) {
          easySubscribeQueryParams.calendarId = String(calendar._id);
        }
        route = getPageRoute(
          "calendar",
          "EASY_SUBSCRIBE",
          { handle },
          easySubscribeQueryParams,
        );
      } else {
        route = getPageRoute("upgrade", "UPGRADE_TRIGGER", {}, queryParams);
      }
    } else {
      // All other custom routes - calendar passed via state; the steps routes
      // also carry it in the path when both params are known.
      const handle = options.handle ?? calendar?.handle;
      const calendarId = options.calendarId ?? calendar?._id;
      route = getPageRoute(
        "help",
        feature.upgradeRoute,
        handle && calendarId ? { handle, calendarId } : undefined
      );
    }
  } else {
    // Default to standard upgrade page
    route = getPageRoute("upgrade", "UPGRADE_PLUS", {}, queryParams);
  }

  return {
    route,
    state,
    queryParams,
    upgradeType: feature.upgradeType,
  };
};

/**
 * Get upgrade route string (convenience function)
 */
export const getUpgradeRoute = (featureKey, options = {}) => {
  const flow = getUpgradeFlow(featureKey, options);
  return flow?.route || getPageRoute("upgrade", "UPGRADE_PLUS");
};

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  Features,
  hasAccess,
  requiresUpgrade,
  isGoogleCalendarConnectionCandidate,
  shouldUseSubscriptionExperience,
  getUpgradeFlow,
  getUpgradeRoute,
};
